import React, { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import * as styles from './EditableCriteria.module.scss';
import { Button, Icon, IconList, getFocusedElement } from 'components/core';
import ReviewEligibilityCriteria from 'components/shared/ReviewEligibilityCriteria';
import { Inline, Stack } from 'components/layout';
import { SidebarBody, SidebarFooter } from 'components/shared/Sidebar';
import { useCriteria } from 'hooks/reviews/useCriteria';

type Props = {
  // True when this mount follows an exit from edit mode, whose Save/Cancel
  // buttons unmounted with focus on them.
  autoFocusEdit?: boolean;
  // Call after applying the focus, so a later remount can't re-steal it.
  onAutoFocusEditHandled: () => void;
  onEnterEdit: () => void;
  // Omitted by hosts that already are the window (the criteria pop-out).
  onOpenWindow?: () => void;
  // Keep the displayed criteria fresh while this panel stays mounted.
  pollForUpdates?: boolean;
  sectionHeadingComponent?: 'h2' | 'h3';
  emptyState?: ReactNode;
};

export const CriteriaPanel = ({
  autoFocusEdit = false,
  onAutoFocusEditHandled,
  onEnterEdit,
  onOpenWindow,
  pollForUpdates = false,
  sectionHeadingComponent,
  emptyState,
}: Props) => {
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const emptyStateRef = useRef<HTMLDivElement>(null);
  // Shares the view's criteria query (same key, deduped). Editing needs the
  // loaded data: the form seeds from it, and its version protects the save.
  const { criteria } = useCriteria({ poll: pollForUpdates });
  const showEmptyState = emptyState != null;

  const prevShowEmptyState = useRef(showEmptyState);
  const focusedBeforeSwap = useRef<HTMLElement | null>(null);
  if (prevShowEmptyState.current !== showEmptyState) {
    prevShowEmptyState.current = showEmptyState;
    focusedBeforeSwap.current = getFocusedElement();
  }

  const focusPanel = () =>
    (editButtonRef.current ?? emptyStateRef.current)?.focus();

  useLayoutEffect(() => {
    const focused = focusedBeforeSwap.current;
    focusedBeforeSwap.current = null;
    if (focused && !focused.isConnected) {
      focusPanel();
    }
  }, [showEmptyState]);

  useEffect(() => {
    if (autoFocusEdit) {
      focusPanel();
      onAutoFocusEditHandled();
    }
  }, [autoFocusEdit, onAutoFocusEditHandled]);

  if (showEmptyState) {
    return (
      <div ref={emptyStateRef} tabIndex={-1} className={styles.EmptyState}>
        {emptyState}
      </div>
    );
  }

  return (
    <React.Fragment key="criteria">
      <SidebarBody className={styles.HeaderDivider}>
        {/* Focusable so keyboard users can scroll overflowing criteria. */}
        <Stack
          spacing={3}
          tabIndex={0}
          role="group"
          aria-label="Review eligibility criteria"
          className={styles.CriteriaGroup}
        >
          <ReviewEligibilityCriteria
            sectionHeadingComponent={sectionHeadingComponent}
          />
        </Stack>
      </SidebarBody>
      <SidebarFooter>
        <Inline alignX="left" spacing={3}>
          <Button
            ref={editButtonRef}
            size="sm"
            type="brand"
            onClick={onEnterEdit}
            disabled={!criteria}
            data-pendo-key="edit-criteria"
            className={styles.SidebarFooterButton}
          >
            Edit
          </Button>
          {onOpenWindow && (
            <Button
              size="sm"
              type="neutral"
              variant="outline"
              onClick={onOpenWindow}
              data-pendo-key="open-criteria-window"
              className={styles.SidebarFooterButton}
            >
              Open in window
              <Icon icon={IconList.light.faArrowUpRightFromSquare} size="md" />
            </Button>
          )}
        </Inline>
      </SidebarFooter>
    </React.Fragment>
  );
};
